import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { HubHeader } from '../components/hub/HubHeader';
import { FilterBar } from '../components/hub/FilterBar';
import { DirectoryColumns } from '../components/hub/DirectoryColumns';
import { HubFaqs } from '../components/hub/HubFaqs';
import { SiblingHubs } from '../components/hub/SiblingHubs';
import { TierNote } from '../components/TierNote';
import { DesignNote } from '../components/DesignNote';
import { parentsFaqs, parentsFilters, parentsIntro, parentsTier3, providers, resourceGuides, serviceCategories, trustNote } from '../data/parents';
export function ParentsHub() {
  const [active, setActive] = useState<string[]>([]);
  function toggle(option: string) {
    setActive(prev => prev.includes(option) ? prev.filter(x => x !== option) : [...prev, option]);
  }
  const filtered = useMemo(() => {
    if (active.length === 0) return providers;
    return providers.filter(p => active.every(a => p.tags.includes(a)));
  }, [active]);
  return <main id="main" className="bg-paper" data-id="element-888" data-mp="30xni7ko1nwax">
      <HubHeader crumb="For parents" meta={`${parentsIntro.count} · ${parentsIntro.updated}`} titleLead="Parent resources in" titleItalic="Santa Barbara" deck={parentsIntro.deck} data-id="element-889" data-mp="30ygka8c25xmq" />

      <Categories data-id="element-890" data-mp="3eplo7mmglc2v" />

      <section aria-labelledby="guides-h" className="bg-paper" data-id="element-891" data-mp="3eq3g6723944z">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-892" data-mp="3erof08vfr98n">
          <TierNote tier="1" label="Full resource guides" note="Same tier as a place page, different shape. Here a Tier 1 page is a compared list, because parents are deciding between options rather than picking a destination." data-id="element-893" data-mp="3jn3nsmel6ey7" />
          <h2 id="guides-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-894" data-mp="1uas3vs9dqprh">
            The four guides parents use most
          </h2>

          <ul className="mt-14 grid gap-x-14 gap-y-10 border-t border-line pt-12 sm:grid-cols-2 lg:grid-cols-4" data-id="element-895" data-mp="1r8njxi4bchk1">
            {resourceGuides.map(guide => <li key={guide.slug} className="group relative border-b border-line pb-7" data-id="element-896" data-mp="216upz5x2df79">
                <p className="label text-muted/70" data-id="element-897" data-mp="1w7i0seipzw1i">{guide.meta}</p>
                <h3 className="mt-3 font-display text-[24px] font-300 leading-snug text-ink" data-id="element-898" data-mp="1w9bci886x6gd">
                  <Link to="/place" className="decoration-olive decoration-1 underline-offset-[6px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-899" data-mp="2a4wv9xntyu0c">
                    {guide.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[13.5px] leading-[1.75] text-muted" data-id="element-900" data-mp="2f02geu9es4nd">
                  {guide.blurb}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <TrustBand data-id="element-901" data-mp="2ou2sxh70sc64" />

      <FilterBar filters={parentsFilters} active={active} onToggle={toggle} data-id="element-902" data-mp="37kg1k65jxszv" />

      <section aria-labelledby="providers-h" className="bg-paper" data-id="element-903" data-mp="37id7nb1a3fpv">
        <div className="mx-auto max-w-shell px-6 py-20 lg:px-10 lg:py-24" data-id="element-904" data-mp="3cira8dk41xgi">
          <TierNote tier="2" label="Provider rows" note="Cost and how to get in are the two facts that matter here, so they lead. Free and sliding-scale options are always listed first." data-id="element-905" data-mp="2yight635kat8" />
          <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-906" data-mp="2orlt7v2foz4m">
            <h2 id="providers-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-907" data-mp="2tl6ubvzbq981">
              Where local parents actually go
            </h2>
            <p className="label text-muted" role="status" data-id="element-908" data-mp="2js97xer7o045">
              {filtered.length} {filtered.length === 1 ? 'provider' : 'providers'}
              {active.length > 0 && ` · ${active.length} filters`}
            </p>
          </div>

          {filtered.length === 0 ? <div className="mt-14 max-w-lg" data-id="element-909" data-mp="3d6jzisgfpgzg">
              <h3 className="font-display text-[30px] font-300 text-ink" data-id="element-910" data-mp="39600fjcaw3ao">
                Nothing matches all of that
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-911" data-mp="34dqgcjxv9xgb">
                Try dropping the age filter — most of these serve every age.
                And if you can’t find what you need, email us; we’ll help you
                look.
              </p>
              <button type="button" onClick={() => setActive([])} className="label mt-8 bg-ink px-7 py-3.5 text-paper transition-colors duration-150 ease-soft hover:bg-olive" data-id="element-912" data-mp="2ph97bzd5guwx">
                Clear filters
              </button>
            </div> : <ul className="mt-12 border-t border-line" data-id="element-913" data-mp="jnhmoz2t83dt">
              {filtered.map(provider => <li key={provider.id} data-id="element-914" data-mp="er0x00fypgym">
                  <article className="grid gap-y-5 border-b border-line py-9 lg:grid-cols-[1.6fr_1fr] lg:gap-x-16" data-id="element-915" data-mp="env56dfy80z7">
                    <div data-id="element-916" data-mp="r81zgr0un7tg">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-917" data-mp="r7a3hf3cb82n">
                        <h3 className="font-display text-[28px] font-300 leading-tight text-ink" data-id="element-918" data-mp="vys2hxlsyykz">
                          {provider.name}
                        </h3>
                        <span className="label text-olive" data-id="element-919" data-mp="3acqzl1vqyee">
                          {provider.category} · {provider.area}
                        </span>
                      </div>
                      <p className="mt-4 max-w-prose text-[15.5px] leading-[1.8] text-muted" data-id="element-920" data-mp="8bjzjmbuk1m4">
                        {provider.blurb}
                      </p>
                      <a href="#faq" className="label mt-5 inline-flex items-center gap-1.5 text-ink underline decoration-olive underline-offset-4" data-id="element-921" data-mp="hb6ym1h3svc0">
                        Provider website
                        <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-922" data-mp="3pposaajpim46" />
                      </a>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 self-start" data-id="element-923" data-mp="3kpsp2diu2ypo">
                      {[['Cost', provider.cost], ['Ages', provider.ages], ['Getting in', provider.access], ['Type', provider.category]].map(([term, value]) => <div key={term} data-id="element-924" data-mp="320mcz9cjp5cu">
                          <dt className="label text-muted/70" data-id="element-925" data-mp="321nnncpnfpu1">{term}</dt>
                          <dd className="mt-1.5 text-[14px] leading-snug text-ink" data-id="element-926" data-mp="3fsal71job9oj">
                            {value}
                          </dd>
                        </div>)}
                    </dl>
                  </article>
                </li>)}
            </ul>}
        </div>
      </section>

      <DirectoryColumns title="Everything else, by category" groups={parentsTier3} data-id="element-927" data-mp="2ic3koiw8tbtx" />
      <HubFaqs faqs={parentsFaqs} data-id="element-928" data-mp="xs86kzkiu0tu" />
      <SiblingHubs exclude={['Parent resources']} data-id="element-929" data-mp="12lksh1xtftiw" />
    </main>;
}
function Categories() {
  return <section aria-labelledby="categories-h" className="border-y border-line bg-white" data-id="element-930" data-mp="ou674ogfrg8o">
      <div className="mx-auto max-w-shell px-6 py-16 lg:px-10 lg:py-20" data-id="element-931" data-mp="tshi52igvwsh">
        <DesignNote label="Category doorways" note="Eight categories, each its own filtered URL in Squarespace. This is the widest hub on the site, so people pick a category before they see any listings." data-id="element-932" data-mp="tqd59v2lbng2" />
        <h2 id="categories-h" className="max-w-2xl font-display text-[38px] font-300 leading-[1.05] text-ink sm:text-[46px]" data-id="element-933" data-mp="17knnrunzuldo">
          What are you looking for?
        </h2>

        <ul className="mt-14 grid gap-x-14 gap-y-12 border-t border-line pt-12 sm:grid-cols-2 lg:grid-cols-4" data-id="element-934" data-mp="3sa3kt7dt36br">
          {serviceCategories.map(category => <li key={category.slug} className="group relative" data-id="element-935" data-mp="11pgvqmjohya">
              <span className={`block h-2 w-14 ${category.swatch}`} aria-hidden="true" data-id="element-936" data-mp="st7y7ss2yebx" />
              <h3 className="mt-6 font-display text-[26px] font-300 leading-tight text-ink" data-id="element-937" data-mp="nuwkh6ynzjof">
                <Link to="/place" className="decoration-olive decoration-1 underline-offset-[7px] after:absolute after:inset-0 after:content-[''] group-hover:underline" data-id="element-938" data-mp="iva9z5a80hjb">
                  {category.name}
                </Link>
              </h3>
              <p className="mt-3 text-[14.5px] leading-[1.8] text-muted" data-id="element-939" data-mp="16le1wfe1qsuh">
                {category.blurb}
              </p>
              <p className="label mt-4 text-muted/70" data-id="element-940" data-mp="wti5d8rlzasd">{category.count}</p>
            </li>)}
        </ul>
      </div>
    </section>;
}
function TrustBand() {
  return <section aria-labelledby="trust-h" className="bg-foam/40" data-id="element-941" data-mp="1uagpe3vd3fww">
      <div className="mx-auto grid max-w-shell gap-10 px-6 py-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20 lg:px-10" data-id="element-942" data-mp="1pgcljthe7o01">
        <h2 id="trust-h" className="font-display text-[34px] font-300 leading-[1.08] text-ink" data-id="element-943" data-mp="1xscye2jjo3mf">
          {trustNote.title}
        </h2>
        <p className="max-w-prose text-[16px] leading-[1.9] text-ink/80" data-id="element-944" data-mp="26q6ogvqpu115">
          {trustNote.body}
        </p>
      </div>
    </section>;
}